import LiveVoicePlayback from "./LiveVoicePlayback";

const SESSION_READY_TIMEOUT_MS = 30000;
const PING_INTERVAL_MS = 30000;
const MAX_SOCKET_BUFFERED_BYTES = 48000;
const RECONNECT_DELAYS_MS = [500, 1000, 2000, 4000, 8000, 10000];
const WORKLET_CONTEXTS = new WeakSet();
const DEFAULT_END_OF_TURN_SILENCE_MS = 2000;
const MIN_END_OF_TURN_SILENCE_MS = 0;
const MAX_END_OF_TURN_SILENCE_MS = 10000;
const SILENCE_RELEASE_FRAME_COUNT = 6; // 600ms at 100ms/frame; enough for server VAD.
const DEFAULT_NOISE_FLOOR = 0.002;
const MIN_SILENCE_LEVEL = 0.003;
const MAX_SILENCE_LEVEL = 0.018;
const MIN_SPEECH_LEVEL = 0.006;
const MAX_SPEECH_LEVEL = 0.03;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const normalizeEndOfTurnSilenceMs = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return DEFAULT_END_OF_TURN_SILENCE_MS;
  return Math.round(clamp(numeric, MIN_END_OF_TURN_SILENCE_MS, MAX_END_OF_TURN_SILENCE_MS));
};

const NON_RECOVERABLE_SESSION_CODES = new Set([
  "unauthorized",
  "authorization_expired",
  "forbidden",
  "identity_required",
  "invalid_device",
  "origin_forbidden",
  "provider_not_configured",
  "provider_quota",
]);

const ERROR_MESSAGES = {
  unauthorized: "Your session is no longer valid. Please sign in again.",
  authorization_expired: "Your session expired. Please sign in again.",
  forbidden: "This organization is not available for your account.",
  identity_required: "The authenticated platform session does not include a user identity.",
  invalid_device: "The selected device is not available in this organization.",
  origin_forbidden: "Live voice is not allowed from this website origin.",
  provider_quota: "The AI voice provider quota is unavailable right now.",
  provider_not_configured: "The AI voice provider is not configured.",
  transcription_unavailable: "Speech recognition is temporarily unavailable.",
  session_unavailable: "The live voice session could not be started.",
  chat_unavailable: "Sama could not prepare a reply. Please try again.",
  turn_timeout: "The AI reply timed out. Please ask again.",
  speech_unavailable: "The reply text is ready, but voice generation failed.",
  speech_timeout: "Voice generation timed out.",
  queue_full: "Too many questions are waiting. Please wait for the current reply.",
  server_busy: "The live voice service is busy. Please try again shortly.",
  history_unavailable: "The conversation could not be saved, but this live session can continue.",
  transcription_failed: "Sama could not understand that audio segment.",
  transcription_timeout: "Speech recognition timed out for that audio segment.",
  transcription_interrupted: "Speech recognition was interrupted for that audio segment.",
  network: "The live voice connection was interrupted.",
  reconnecting: "The live voice connection was interrupted. Reconnecting…",
  timeout: "The live voice session did not become ready in time.",
  backpressure: "Your connection cannot upload microphone audio in real time.",
  audio_sequence: "The AI audio stream arrived out of order.",
  audio_format: "The AI audio stream has an unsupported format.",
  audio_context: "Browser audio stopped unexpectedly.",
  NotAllowedError: "Microphone access is blocked. Allow microphone access and try again.",
  NotFoundError: "No microphone was found.",
  NotReadableError: "The microphone could not be opened. Another app may be using it.",
  unsupported_browser: "Live voice requires a browser with microphone and AudioWorklet support.",
  invalid_websocket_url: "The live voice WebSocket URL is invalid.",
  credentials_in_url: "Credentials are not allowed in the live voice URL.",
  secure_websocket_required: "This secure page requires a wss:// live voice endpoint.",
  idle_timeout: "The live voice session ended because it was inactive.",
  session_timeout: "The live voice session reached its maximum duration.",
};

export const getLiveVoiceErrorMessage = (code, fallback = "") =>
  ERROR_MESSAGES[code] || fallback || `Live voice error (${code || "unknown"}).`;

const validateWebSocketUrl = (value) => {
  let endpoint;
  try {
    endpoint = new URL(String(value || "").trim());
  } catch {
    throw new Error("invalid_websocket_url");
  }

  if (!["ws:", "wss:"].includes(endpoint.protocol)) throw new Error("invalid_websocket_url");
  if (endpoint.username || endpoint.password || endpoint.search || endpoint.hash) {
    throw new Error("credentials_in_url");
  }
  if (typeof window !== "undefined" && window.location?.protocol === "https:" && endpoint.protocol !== "wss:") {
    throw new Error("secure_websocket_required");
  }

  return endpoint.href;
};

export default class LiveVoiceClient {
  constructor({
    onEvent,
    onState,
    onLevel,
    onError,
    onNotice,
    workletUrl,
  }) {
    this.onEvent = onEvent;
    this.onState = onState;
    this.onLevel = onLevel;
    this.onError = onError;
    this.onNotice = onNotice;
    this.workletUrl = workletUrl;
    this.closed = false;
    this.ready = false;
    this.intentionalClose = false;
    this.ownsContext = false;
    this.reconnectAttempt = 0;
    this.connectionGeneration = 0;
    this.sessionId = "";
    this.initialToken = "";
    this.getToken = null;
    this.endOfTurnSilenceMs = DEFAULT_END_OF_TURN_SILENCE_MS;
    this.noiseFloor = DEFAULT_NOISE_FLOOR;
    this.localSpeechActive = false;
    this.heldSilenceFrames = [];
    this.silenceReleaseTimer = null;
  }

  send(event) {
    if (!this.closed && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(event));
      return true;
    }
    return false;
  }

  async start({
    url,
    token,
    getToken,
    organizationId,
    sessionId,
    deviceId,
    voice,
    endOfTurnSilenceMs,
    audioContext,
  }) {
    this.onState?.("connecting");

    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.AudioWorkletNode) {
        throw new Error("unsupported_browser");
      }

      this.endpoint = validateWebSocketUrl(url);
      this.initialToken = String(token || "").replace(/^Bearer\s+/i, "").trim();
      this.getToken = typeof getToken === "function" ? getToken : null;
      this.organizationId = Number(organizationId);
      this.sessionId = String(sessionId || "").trim();
      this.deviceId = deviceId ? String(deviceId) : "";
      this.voice = voice ? String(voice) : "";
      this.endOfTurnSilenceMs = normalizeEndOfTurnSilenceMs(endOfTurnSilenceMs);
      this.resetTurnSilenceGate();

      if (!this.initialToken && !this.getToken) throw new Error("unauthorized");
      if (!Number.isSafeInteger(this.organizationId) || this.organizationId <= 0) {
        throw new Error("forbidden");
      }

      this.context = audioContext || new AudioContext({ sampleRate: 24000, latencyHint: "interactive" });
      this.ownsContext = !audioContext;
      if (this.context.state !== "running") await this.context.resume();
      if (this.closed) return;

      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (this.closed) {
        this.stream.getTracks().forEach((track) => track.stop());
        return;
      }

      if (!WORKLET_CONTEXTS.has(this.context)) {
        await this.context.audioWorklet.addModule(this.workletUrl);
        WORKLET_CONTEXTS.add(this.context);
      }
      if (this.closed) return;

      this.playback = new LiveVoicePlayback({
        context: this.context,
        send: (event) => this.send(event),
        onIdle: () => {
          if (this.ready) this.onState?.("listening");
        },
      });

      this.source = this.context.createMediaStreamSource(this.stream);
      this.worklet = new AudioWorkletNode(this.context, "sama-live-voice-mic", {
        channelCount: 1,
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      this.silentGain = this.context.createGain();
      this.silentGain.gain.value = 0;

      // Keep the microphone capture alive for the whole activated Hey Sama session.
      // Frames are sent only while the socket is ready. During reconnects they are
      // intentionally dropped instead of buffered/replayed as stale speech.
      this.worklet.port.onmessage = ({ data }) => {
        if (this.closed) return;
        const level = Math.max(0, Number(data?.level) || 0);
        this.onLevel?.(level);
        if (!this.ready || this.socket?.readyState !== WebSocket.OPEN || !data?.pcm) return;
        this.handleMicrophoneFrame(data.pcm, level);
      };
      this.worklet.onprocessorerror = () => this.fail("audio_context");

      const audioTrack = this.stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          if (!this.closed) this.fail("NotReadableError");
        };
      }

      this.source.connect(this.worklet);
      this.worklet.connect(this.silentGain);
      this.silentGain.connect(this.context.destination);

      this.pageHideHandler = () => {
        if (this.closed) return;
        this.intentionalClose = true;
        this.send({ type: "session.end" });
        this.dispose();
      };
      window.addEventListener("pagehide", this.pageHideHandler, { once: true });

      this.connectSocket();
    } catch (error) {
      if (!this.closed) {
        const code = error?.message || error?.name || "network";
        this.onError?.(getLiveVoiceErrorMessage(code, error?.message), {
          code,
          fatal: true,
          recoverable: false,
        });
        this.dispose();
      }
    }
  }

  sendPcmFrame(pcm) {
    if (
      this.closed ||
      !this.ready ||
      !pcm ||
      this.socket?.readyState !== WebSocket.OPEN
    ) {
      return false;
    }

    if (this.socket.bufferedAmount > MAX_SOCKET_BUFFERED_BYTES) {
      this.recoverTransport("backpressure");
      return false;
    }

    this.socket.send(pcm);
    return true;
  }

  getSilenceLevelThreshold() {
    return clamp(this.noiseFloor * 2.2, MIN_SILENCE_LEVEL, MAX_SILENCE_LEVEL);
  }

  getSpeechLevelThreshold() {
    return clamp(this.noiseFloor * 3.2, MIN_SPEECH_LEVEL, MAX_SPEECH_LEVEL);
  }

  updateNoiseFloor(level) {
    if (!Number.isFinite(level) || level < 0 || this.localSpeechActive) return;
    // Slow EMA keeps the threshold adapted to the user's room without following
    // short speech peaks. Browser noise suppression runs before this measurement.
    const capped = Math.min(level, 0.05);
    this.noiseFloor = (this.noiseFloor * 0.96) + (capped * 0.04);
  }

  clearHeldSilence({ discard = true } = {}) {
    window.clearTimeout(this.silenceReleaseTimer);
    this.silenceReleaseTimer = null;
    if (discard) this.heldSilenceFrames = [];
  }

  resetTurnSilenceGate() {
    this.clearHeldSilence();
    this.localSpeechActive = false;
    this.noiseFloor = DEFAULT_NOISE_FLOOR;
  }

  markSpeechActive() {
    if (!this.localSpeechActive) this.localSpeechActive = true;
    // A short pause followed by speech is part of the same utterance: drop only
    // the withheld silence. Speech itself is never delayed or buffered.
    this.clearHeldSilence();
  }

  scheduleTerminalSilenceRelease() {
    if (this.silenceReleaseTimer || this.endOfTurnSilenceMs <= 0) return;
    this.silenceReleaseTimer = window.setTimeout(() => {
      this.silenceReleaseTimer = null;
      if (this.closed || !this.ready || this.socket?.readyState !== WebSocket.OPEN) {
        this.heldSilenceFrames = [];
        return;
      }

      // We only need enough silent PCM for the AI server's VAD to observe the
      // end of turn. Sending the entire held pause would waste bandwidth and can
      // unnecessarily queue silence after the turn has already ended.
      const frames = this.heldSilenceFrames.splice(0, SILENCE_RELEASE_FRAME_COUNT);
      this.heldSilenceFrames = [];
      for (const frame of frames) {
        if (!this.sendPcmFrame(frame)) break;
      }

      // Continue streaming subsequent idle silence normally while the server
      // finalizes this turn. A new speech.started event will arm the gate again.
      this.localSpeechActive = false;
    }, this.endOfTurnSilenceMs);
  }

  handleMicrophoneFrame(pcm, level) {
    if (this.endOfTurnSilenceMs <= 0) {
      this.updateNoiseFloor(level);
      this.sendPcmFrame(pcm);
      return;
    }

    const silenceThreshold = this.getSilenceLevelThreshold();
    const speechThreshold = this.getSpeechLevelThreshold();

    if (!this.localSpeechActive) {
      this.updateNoiseFloor(level);
      if (level >= speechThreshold) {
        this.markSpeechActive();
      }
      this.sendPcmFrame(pcm);
      return;
    }

    // Hysteresis: once speech is active, values above the lower silence threshold
    // continue to count as speech. This avoids clipping quiet trailing syllables.
    if (level > silenceThreshold) {
      this.markSpeechActive();
      this.sendPcmFrame(pcm);
      return;
    }

    // Hold only quiet frames. If the user resumes speaking before the configured
    // grace period, these frames are discarded and the same server-side turn stays
    // open. If silence continues, a short silent tail is released for server VAD.
    this.heldSilenceFrames.push(pcm);
    const maxHeldFrames = Math.max(
      SILENCE_RELEASE_FRAME_COUNT,
      Math.ceil(this.endOfTurnSilenceMs / 100) + SILENCE_RELEASE_FRAME_COUNT
    );
    if (this.heldSilenceFrames.length > maxHeldFrames) {
      this.heldSilenceFrames.splice(0, this.heldSilenceFrames.length - maxHeldFrames);
    }
    this.scheduleTerminalSilenceRelease();
  }

  setEndOfTurnSilenceMs(value) {
    this.endOfTurnSilenceMs = normalizeEndOfTurnSilenceMs(value);
    // Apply configuration changes safely without replaying any quiet PCM that was
    // captured under the previous timing. The next quiet frame starts the new window.
    this.clearHeldSilence();
  }

  setDeviceId(value) {
    const nextDeviceId = String(value ?? "").trim();
    if (nextDeviceId === this.deviceId) return;

    this.deviceId = nextDeviceId;

    // IMPORTANT: a server chat session can retain the device scope/history that
    // was established when that session_id was first created. Reusing the same
    // session_id after navigating to another Device Details page can therefore
    // make the AI keep answering for the previous device even though a new
    // device_id is present in session.start. A device-context change must start a
    // fresh server chat session. The live microphone stays armed; only the
    // WebSocket transport/session is refreshed.
    this.sessionId = "";

    if (this.closed || this.intentionalClose || !this.endpoint) return;

    // device_id belongs to session.start, so an already-open socket cannot safely
    // mutate its device scope in-place. Reconnect the transport with a NEW server
    // session and the current-page device scope. Microphone capture stays alive
    // and no buffered/stale audio is replayed.
    this.ready = false;
    this.resetTurnSilenceGate();
    window.clearTimeout(this.readyTimer);
    window.clearTimeout(this.reconnectTimer);
    window.clearInterval(this.pingTimer);
    this.playback?.clear();

    const oldSocket = this.socket;
    this.socket = null;
    this.connectionGeneration += 1;
    if (oldSocket) {
      oldSocket.onopen = null;
      oldSocket.onmessage = null;
      oldSocket.onerror = null;
      oldSocket.onclose = null;
      try {
        oldSocket.close(1000, "device_context_changed");
      } catch {}
    }

    this.reconnectAttempt = 0;
    this.onState?.("connecting");
    this.connectSocket();
  }

  getFreshToken() {
    const provided = this.getToken?.();
    const clean = String(provided || this.initialToken || "")
      .replace(/^Bearer\s+/i, "")
      .trim();
    this.initialToken = "";
    return clean;
  }

  connectSocket() {
    if (this.closed || this.intentionalClose) return;

    window.clearTimeout(this.reconnectTimer);
    window.clearTimeout(this.readyTimer);
    window.clearInterval(this.pingTimer);
    this.ready = false;

    const generation = ++this.connectionGeneration;
    let socket;
    try {
      socket = new WebSocket(this.endpoint);
    } catch (error) {
      this.recoverTransport(error?.message || "network");
      return;
    }

    this.socket = socket;
    socket.binaryType = "arraybuffer";
    this.readyTimer = window.setTimeout(() => {
      if (this.closed || generation !== this.connectionGeneration || this.ready) return;
      this.recoverTransport("timeout", socket);
    }, SESSION_READY_TIMEOUT_MS);

    socket.onopen = () => {
      if (this.closed || generation !== this.connectionGeneration || socket !== this.socket) return;
      const cleanToken = this.getFreshToken();
      if (!cleanToken) {
        this.fail("unauthorized");
        return;
      }

      socket.send(JSON.stringify({
        type: "session.start",
        authorization: `Bearer ${cleanToken}`,
        organization_id: this.organizationId,
        audio_format: "pcm16",
        sample_rate: 24000,
        ...(this.sessionId ? { session_id: this.sessionId } : {}),
        ...(this.deviceId ? { device_id: this.deviceId } : {}),
        ...(this.voice ? { voice: this.voice } : {}),
      }));
    };

    socket.onmessage = ({ data }) => {
      if (this.closed || generation !== this.connectionGeneration || socket !== this.socket || typeof data !== "string") {
        return;
      }
      try {
        this.handle(JSON.parse(data));
      } catch (error) {
        this.fail(error?.message || "audio_format");
      }
    };

    // Browsers usually follow `error` with `close`; let `close` own recovery so
    // we do not create duplicate reconnect loops.
    socket.onerror = () => {};

    socket.onclose = (event) => {
      if (
        this.closed ||
        this.intentionalClose ||
        generation !== this.connectionGeneration ||
        socket !== this.socket
      ) {
        return;
      }
      this.recoverTransport("network", socket, {
        closeCode: event?.code,
        closeReason: event?.reason,
        wasClean: event?.wasClean,
      });
    };
  }

  recoverTransport(code = "network", socket = this.socket, details = {}) {
    if (this.closed || this.intentionalClose) return;
    if (socket && socket !== this.socket) return;

    this.ready = false;
    this.resetTurnSilenceGate();
    window.clearTimeout(this.readyTimer);
    window.clearInterval(this.pingTimer);
    this.playback?.clear();

    const oldSocket = this.socket;
    this.socket = null;
    if (oldSocket) {
      oldSocket.onopen = null;
      oldSocket.onmessage = null;
      oldSocket.onerror = null;
      oldSocket.onclose = null;
      try {
        oldSocket.close();
      } catch {}
    }

    this.onState?.("reconnecting");
    this.onNotice?.(getLiveVoiceErrorMessage("reconnecting"), {
      code,
      recoverable: true,
      ...details,
    });

    const delay = RECONNECT_DELAYS_MS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)];
    this.reconnectAttempt += 1;
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = window.setTimeout(() => this.connectSocket(), delay);
  }

  handle(event) {
    if (!event || typeof event !== "object") return;

    try {
      switch (event.type) {
        case "session.starting":
          this.onState?.("connecting");
          break;

        case "session.ready": {
          const input = event.input_audio || {};
          const output = event.output_audio || {};
          if (
            (input.sample_rate && input.sample_rate !== 24000) ||
            (output.sample_rate && output.sample_rate !== 24000) ||
            (input.format && input.format !== "pcm16") ||
            (output.format && output.format !== "pcm16") ||
            (input.channels && input.channels !== 1) ||
            (output.channels && output.channels !== 1)
          ) {
            this.fail("audio_format");
            return;
          }

          window.clearTimeout(this.readyTimer);
          this.ready = true;
          this.resetTurnSilenceGate();
          this.reconnectAttempt = 0;
          if (event.session_id) this.sessionId = String(event.session_id);
          this.startPing();
          this.onState?.("listening");
          break;
        }

        case "speech.started":
          this.markSpeechActive();
          if (!this.playback?.responseId) this.onState?.("hearing");
          break;

        case "speech.stopped":
          this.clearHeldSilence();
          this.localSpeechActive = false;
          if (!this.playback?.responseId) this.onState?.("thinking");
          break;

        case "transcript.ignored":
          if (!this.playback?.responseId) this.onState?.("listening");
          break;

        case "response.started":
          this.playback?.begin(event.response_id);
          this.onState?.("thinking");
          break;

        case "audio.delta":
          if (this.playback?.append(event.response_id, event.audio, event.sequence)) {
            this.onState?.("speaking");
          }
          break;

        case "response.done":
          this.playback?.finish(event.response_id, event.playback_ack_required);
          break;

        case "response.cancelled":
          this.playback?.clear();
          this.onState?.("listening");
          break;

        case "session.error":
          if (event.audio_clear) this.playback?.clear();
          if (event.fatal === false) {
            this.onNotice?.(getLiveVoiceErrorMessage(event.code, event.message), event);
          } else if (NON_RECOVERABLE_SESSION_CODES.has(event.code)) {
            this.onError?.(getLiveVoiceErrorMessage(event.code, event.message), {
              ...event,
              fatal: true,
              recoverable: false,
            });
            this.dispose();
          } else {
            this.recoverTransport(event.code || "session_unavailable", this.socket, event);
          }
          break;

        case "session.closed":
          if (event.reason === "client_end") {
            this.dispose();
          } else if (NON_RECOVERABLE_SESSION_CODES.has(event.reason)) {
            this.onError?.(getLiveVoiceErrorMessage(event.reason, event.message), {
              ...event,
              fatal: true,
              recoverable: false,
            });
            this.dispose();
          } else {
            this.recoverTransport(event.reason || "network", this.socket, event);
          }
          break;

        case "response.error":
          this.playback?.clear();
          this.onState?.("listening");
          this.onNotice?.(getLiveVoiceErrorMessage(event.code, event.message), event);
          break;

        case "turn.rejected":
          if (!this.playback?.responseId) this.onState?.("listening");
          this.onNotice?.(getLiveVoiceErrorMessage(event.code, event.message), event);
          break;

        case "transcript.error":
          if (!this.playback?.responseId) this.onState?.("listening");
          this.onNotice?.(getLiveVoiceErrorMessage(event.code, event.message), event);
          break;

        case "audio.error":
        case "history.error":
          this.onNotice?.(getLiveVoiceErrorMessage(event.code, event.message), event);
          break;

        default:
          break;
      }
    } catch (error) {
      this.fail(error?.message || "audio_format");
      return;
    }

    this.onEvent?.(event);
  }

  startPing() {
    window.clearInterval(this.pingTimer);
    this.pingTimer = window.setInterval(() => {
      this.send({ type: "ping" });
    }, PING_INTERVAL_MS);
  }

  stopReply() {
    this.playback?.clear();
    this.send({ type: "response.cancel" });
    this.onState?.("listening");
  }

  end() {
    if (this.closed) return;
    this.intentionalClose = true;
    this.send({ type: "session.end" });
    window.setTimeout(() => this.dispose(), 0);
  }

  fail(code) {
    if (this.closed) return;
    this.onError?.(getLiveVoiceErrorMessage(code), {
      code,
      fatal: true,
      recoverable: false,
    });
    this.dispose();
  }

  dispose() {
    if (this.closed) return;
    this.closed = true;
    this.ready = false;
    this.clearHeldSilence();
    this.localSpeechActive = false;
    window.clearTimeout(this.readyTimer);
    window.clearTimeout(this.reconnectTimer);
    window.clearInterval(this.pingTimer);
    if (this.pageHideHandler) {
      window.removeEventListener("pagehide", this.pageHideHandler);
      this.pageHideHandler = null;
    }

    this.playback?.clear();
    this.stream?.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch {}
    });

    if (this.worklet) {
      this.worklet.port.onmessage = null;
      try {
        this.worklet.disconnect();
      } catch {}
    }
    try {
      this.source?.disconnect();
    } catch {}
    try {
      this.silentGain?.disconnect();
    } catch {}

    if (this.ownsContext && this.context && this.context.state !== "closed") {
      this.context.close().catch(() => {});
    }

    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      try {
        this.socket.close();
      } catch {}
    }

    this.onLevel?.(0);
    this.onState?.("idle");
  }
}
